import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { RequireAuth } from './components/RequireAuth';
import { ActiveGroupProvider } from './state/activeGroup';
import { NavHistoryProvider } from './state/navHistory';
import { HomePage } from './pages/Home';
import { LoginPage } from './pages/Login';
import { SignupPage } from './pages/Signup';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { JoinGroupPage } from './pages/JoinGroup';
import { SchedulePage } from './pages/Schedule';
import { MatchDetailPage } from './pages/MatchDetail';
import { StandingsPage } from './pages/Standings';
import { TeamsPage } from './pages/Teams';
import { TeamDetailPage } from './pages/TeamDetail';
import { StadiumsPage } from './pages/Stadiums';
import { StadiumDetailPage } from './pages/StadiumDetail';
import { ProfilePage } from './pages/Profile';
import { RerollPage } from './pages/Reroll';
import { PaymentSuccessPage } from './pages/PaymentSuccess';
import { PaymentCancelPage } from './pages/PaymentCancel';
import { UpgradePage } from './pages/Upgrade';
import { AdminMatchesPage } from './pages/admin/AdminMatches';
import { AdminUsersPage } from './pages/admin/AdminUsers';
import { AdminSettingsPage } from './pages/admin/AdminSettings';
import { AdminSeedPage } from './pages/admin/AdminSeed';
import { AdminJobsPage } from './pages/admin/AdminJobs';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

// One client for the app lifetime. We keep cache short because match data
// changes live; components that need tighter cadence can override on the query.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ActiveGroupProvider>
        <BrowserRouter>
          <NavHistoryProvider>
          <Routes>
            <Route element={<App />}>
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              <Route path="verify-email" element={<VerifyEmailPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route
                path="accept_group_invite/:token"
                element={<AcceptInvitePage />}
              />
              <Route path="join/:shortId" element={<JoinGroupPage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="matches/:id" element={<MatchDetailPage />} />
              <Route path="teams" element={<TeamsPage />} />
              <Route path="teams/:id" element={<TeamDetailPage />} />
              <Route path="stadiums" element={<StadiumsPage />} />
              <Route path="stadiums/:id" element={<StadiumDetailPage />} />
              <Route
                path="standings"
                element={
                  <RequireAuth redirectTo="/">
                    <StandingsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="profile"
                element={
                  <RequireAuth>
                    <ProfilePage />
                  </RequireAuth>
                }
              />
              <Route
                path="reroll"
                element={
                  <RequireAuth>
                    <RerollPage />
                  </RequireAuth>
                }
              />
              <Route path="upgrade" element={<UpgradePage />} />
              <Route
                path="payment/success"
                element={
                  <RequireAuth>
                    <PaymentSuccessPage />
                  </RequireAuth>
                }
              />
              <Route path="payment/cancel" element={<PaymentCancelPage />} />
              <Route
                path="admin/matches"
                element={
                  <RequireAuth requireAdmin>
                    <AdminMatchesPage />
                  </RequireAuth>
                }
              />
              <Route
                path="admin/users"
                element={
                  <RequireAuth requireAdmin>
                    <AdminUsersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="admin/settings"
                element={
                  <RequireAuth requireAdmin>
                    <AdminSettingsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="admin/seed"
                element={
                  <RequireAuth requireAdmin>
                    <AdminSeedPage />
                  </RequireAuth>
                }
              />
              <Route
                path="admin/jobs"
                element={
                  <RequireAuth requireAdmin>
                    <AdminJobsPage />
                  </RequireAuth>
                }
              />
            </Route>
          </Routes>
          </NavHistoryProvider>
        </BrowserRouter>
      </ActiveGroupProvider>
    </QueryClientProvider>
  </StrictMode>,
);
